import React, { createContext, useContext, useState, useEffect } from 'react';
import { useIsAuthenticated } from "@azure/msal-react";
import { ShoppingCartService } from '../services/shoppingCartService';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItemCount, setCartItemCount] = useState(0);
  const [cart, setCart] = useState({ items: [], totalPrice: '€ 0.00' });
  const [cartLoaded, setCartLoaded] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const shoppingCartService = ShoppingCartService();

  const fetchCart = async () => {
    if (!isAuthenticated) {
      setCartItemCount(0);
      setCart({ items: [], totalPrice: '€ 0.00' });
      setCartLoaded(true);
      return;
    }

    try {
      const fetchedCart = await shoppingCartService.getShoppingCart();
      if (fetchedCart && fetchedCart.items) {
        setCartItemCount(fetchedCart.items.length);
        setCart(fetchedCart);
      } else {
        setCartItemCount(0);
        setCart({ items: [], totalPrice: '€ 0.00' });
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCartItemCount(0);
      setCart({ items: [], totalPrice: '€ 0.00' });
    } finally {
      setCartLoaded(true);
    }
  };

  const addItem = async (itemId, quantity = 1) => {
    try {
      console.log('CartContext: Adding item', itemId, 'quantity:', quantity);
      const updatedCart = await shoppingCartService.addItem(itemId, quantity);
      console.log('CartContext: Updated cart received:', updatedCart);
      if (updatedCart && updatedCart.items) {
        setCartItemCount(updatedCart.items.length);
        setCart(updatedCart);
        console.log('CartContext: Cart state updated. Items count:', updatedCart.items.length);
      }
      return updatedCart;
    } catch (error) {
      console.error('Error adding item to cart:', error);
      throw error;
    }
  };

  const removeItem = async (item) => {
    try {
      const updatedCart = await shoppingCartService.removeItem(item);
      if (updatedCart && updatedCart.items) {
        setCartItemCount(updatedCart.items.length);
        setCart(updatedCart);
      }
      return updatedCart;
    } catch (error) {
      console.error('Error removing item from cart:', error);
      throw error;
    }
  };

  const removeTotalItem = async (item) => {
    try {
      const updatedCart = await shoppingCartService.removeTotalItem(item);
      if (updatedCart && updatedCart.items) {
        setCartItemCount(updatedCart.items.length);
        setCart(updatedCart);
      }
      return updatedCart;
    } catch (error) {
      console.error('Error removing total item from cart:', error);
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      const updatedCart = await shoppingCartService.clearCart();
      setCartItemCount(0);
      setCart({ items: [], totalPrice: '€ 0.00' });
      return updatedCart;
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  };

  const placeOrder = async (orderData) => {
    try {
      const result = await shoppingCartService.placeOrder(orderData);
      if (result.success && result.activeShoppingCart) {
        setCartItemCount(result.activeShoppingCart.items?.length || 0);
        setCart(result.activeShoppingCart);
      }
      return result;
    } catch (error) {
      console.error('Error placing order:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchCart();
  }, [isAuthenticated]);

  const value = {
    cartItemCount,
    cart,
    cartLoaded,
    fetchCart,
    addItem,
    removeItem,
    removeTotalItem,
    clearCart,
    placeOrder,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
